/**
 * usePaymentsReimport — R5-RI
 *
 * Mutation hook for POST /api/v2/payments/reimport — v1-Paritaet zu
 * payments_controller.rb#reimport: stoesst fuer ALLE eigenen Wallets des Users
 * einen Payment-Sync (WalletWorker#update_payments!) an. User-weit (kein
 * Wallet-Scoping wie beim Hotspot-Reimport), Rate-Limit 1x/15min PRO USER
 * (teurer, da N Wallets x Solana-RPC-Calls). Kein Status-Endpoint — v1-Paritaet
 * bewusst nur "Import dauert einige Minuten" (siehe R5-Entscheidungen.md #RI).
 *
 * R5-RI: /api/v2/payments/reimport wurde additiv nach api-types/openapi.yaml
 * nachgezogen (kein `as any`-Workaround). Rate-Limit-Fehlerbehandlung via
 * apiErrorCode/apiErrorStatus (lib/errors/apiErrorMessage.ts) — Muster analog
 * useSubscriptionHistory.ts.
 *
 * i18n100-1: der Hook wirft stabile Fehler-CODES statt Klartext — PaymentsPage/
 * PaymentsSidebar (Owner-UI) mappt den Code via t().
 */
import { useCallback, useState } from 'react'
import apiClient from '../../lib/api-client'
import { apiErrorCode, apiErrorStatus } from '../../lib/errors/apiErrorMessage'

export type PaymentsReimportErrorCode = 'RATE_LIMITED' | 'REIMPORT_FAILED'

export type PaymentsReimportResult =
  | { ok: true; queued: boolean; walletCount: number; message: string }
  | { ok: false; error: PaymentsReimportErrorCode; retryAfter?: number }

async function paymentsReimportRequest(): Promise<PaymentsReimportResult> {
  try {
    const result = await apiClient.POST('/api/v2/payments/reimport')

    if (result.error) {
      return { ok: false, error: 'REIMPORT_FAILED' }
    }

    const json = result.data as { data?: { queued?: boolean; wallet_count?: number; message?: string } }
    const data = json?.data

    return {
      ok: true,
      queued: data?.queued ?? true,
      walletCount: data?.wallet_count ?? 0,
      message: data?.message ?? '',
    }
  } catch (e) {
    if (apiErrorCode(e) === 'rate_limited' || apiErrorStatus(e) === 429) {
      const body = e && typeof e === 'object' && 'body' in e ? (e as { body?: unknown }).body : undefined
      const retryAfter =
        body && typeof body === 'object' && 'retry_after' in body
          ? (body as { retry_after?: number }).retry_after
          : undefined
      return { ok: false, error: 'RATE_LIMITED', retryAfter }
    }
    return { ok: false, error: 'REIMPORT_FAILED' }
  }
}

export function usePaymentsReimport() {
  const [isPending, setIsPending] = useState(false)

  const reimportPayments = useCallback(async (): Promise<PaymentsReimportResult> => {
    setIsPending(true)
    try {
      return await paymentsReimportRequest()
    } finally {
      setIsPending(false)
    }
  }, [])

  return { reimportPayments, isPending }
}
